import { type Accessor, createSignal } from "solid-js";
import type { PersistedSession, SessionStore } from "../sdk/storage.ts";

/** The minimal client surface the session controller drives. */
export interface TokenSink {
  setToken(jwt: string | null): void;
}

/** Reactive session state plus the two transitions that mutate it. */
export interface SessionController {
  /** Reactive persisted session, or `null` when signed out. */
  session: Accessor<PersistedSession | null>;
  /** Persist + activate a session after login or rehydration. */
  setSession(next: PersistedSession): void;
  /** Drop the local session (server revocation is a separate call). */
  clearSession(): void;
}

/**
 * Wire a `SessionStore` and a token sink (the SDK client) into a single
 * reactive session source of truth. Extracted from `AuthProvider` so the
 * hydrate → set → clear lifecycle is testable without rendering a component.
 *
 * On construction it hydrates from the store and seeds the sink's token so the
 * client and the signal start in sync.
 */
export function createSessionController(store: SessionStore, sink: TokenSink): SessionController {
  const initial = store.read();
  const [session, setSessionState] = createSignal<PersistedSession | null>(initial);
  sink.setToken(initial?.jwt ?? null);

  return {
    session,
    setSession(next: PersistedSession) {
      store.write(next);
      sink.setToken(next.jwt);
      setSessionState(next);
    },
    clearSession() {
      store.clear();
      sink.setToken(null);
      setSessionState(null);
    },
  };
}
